(function segment(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Analytics = global.NewHomeSource.Analytics || {};
  const culture = document.documentElement.lang || 'en';
  const isCna = !(culture === 'en');

  if (isCna) {
    global.NewHomeSource.Analytics.segment = function () {};
    return;
  }

  const previousSegment = global.NewHomeSource.Analytics.segment;
  const configurations = {
    eventPrefix: 'segmentEvent',
    identityPrefix: 'segmentIdentity',
    impressionPrefix: 'segmentImpression',
    sharedPrefix: 'segmentShared'
  };

  /**
   * @private
   * append: creates new properties into the reference value.
   *
   * @param {Array} entry key/value pair.
   */
  function append(entry) {
    this[entry[0]] = entry[1];
  }

  /**
   * @private
   * adds traits from configurations.data to the object containing the traits about to log.
   *
   * @param {object} configurationObject object with traits about to log.
   */
  function setDataAttributeTraits(configurationObject) {
    const configurationObj = configurationObject;
    Object.keys(configurations.data).forEach((key) => {
      const value = typeof configurations.data[key].getValue === 'function'
        ? configurations.data[key].getValue(true)
        : configurations.data[key];
      configurationObj[key] = value;
    });
  }

  /**
 * @private
 * setAnonymousId: set the Anonymous id for Automation testing
 */
  function setAnonymousId(segmentAnonymousId) {
    if (segmentAnonymousId !== '' && window.analytics.user) {
      window.analytics.user().anonymousId(segmentAnonymousId);
    }
  }

  /**
  * @private
  * removes null or empty values from traits before sending them.
  *
  * @param {object} traits to be cleaned up.
  */
  function cleanEmptyOrNullTraitsBeforeSend(traits) {
    const currentTraits = traits;
    const propNames = Object.getOwnPropertyNames(currentTraits);
    for (let i = 0; i < propNames.length; i += 1) {
      const propName = propNames[i];
      if (currentTraits[propName] === null ||
        currentTraits[propName] === 0 || currentTraits[propName] === '' ||
        currentTraits[propName] === undefined) {
        delete currentTraits[propName];
      } else {
        currentTraits[propName] = typeof currentTraits[propName] === 'string'
          ? currentTraits[propName].replace(/['\\]/g, '')
          : currentTraits[propName];
      }
    }

    return currentTraits;
  }

  /**
   * @private
   * create the GlobalTratis object.
   *
   * @param {object} status of the scheduled idle task.
   */
  function createGlobalTraits(status) {
    if (!configurations.globalTraits) {
      configurations.globalTraits = {};
    }

    setDataAttributeTraits(configurations.globalTraits);
    setAnonymousId(configurations.data.segmentAnonymousId());
    const timeLapsed = +new Date() - status.time;
    const traits = {
      page_type: configurations.globalTraits.page_type(),
      current_page_number: configurations.globalTraits.current_page_number,
      response_version: configurations.globalTraits.response_version,
      partner_id: configurations.globalTraits.partner_id,
      partner_name: configurations.globalTraits.partner_name,
      brand_partner_name: configurations.globalTraits.brand_partner_name,
      brand_partner_name_partner_name: configurations.globalTraits.brandPartnerNamePartnerName,
      builder_id: configurations.globalTraits.builder_id,
      builder_name: configurations.globalTraits.builder_name,
      city: configurations.globalTraits.city,
      state: configurations.globalTraits.state,
      zip: configurations.globalTraits.zip,
      code_version: configurations.globalTraits.codeBuildeVersion,
      test_version: configurations.globalTraits.abTestsOnThisPage,
      community_id: configurations.globalTraits.communityId,
      market_id: configurations.globalTraits.marketId,
      market_name: configurations.globalTraits.marketName,
      community_high_price: configurations.globalTraits.community_high_price,
      community_low_price: configurations.globalTraits.community_low_price,
      community_name: configurations.globalTraits.community_name,
      primary_image_url: configurations.globalTraits.primary_image_url,
      home_price: configurations.globalTraits.home_price,
      home_type: configurations.globalTraits.home_type,
      spec_id: configurations.globalTraits.spec_id,
      plan_id: configurations.globalTraits.plan_id,
      result_count: configurations.globalTraits.result_count()
    };

    const propNames = Object.getOwnPropertyNames(traits);
    for (let i = 0; i < propNames.length; i += 1) {
      const propName = propNames[i];
      if (traits[propName] === 'undefined' || traits[propName] === null ||
        traits[propName] === 0 || traits[propName] === '' || traits[propName] === '0') {
        delete traits[propName];
      }
    }
    if (traits.Segment === null && timeLapsed < 10000) {
      configurations.globalTraits = null;
      return null;
    }
    return traits;
  }

  /**
   * @private
   * getUserId: get user ID.
   */
  function getUserId() {
    const userIdData = configurations.data.userId() || configurations.data.i_uid.getValue(true);

    const userId = userIdData.replace(/[{}]/g, '');

    return userId;
  }

  /**
   * @private
   * create the GlobalTratis object.
   *
   * @param {object} status of the scheduled idle task.
   */
  function createGlobalIdentifyTraits(status) {
    if (!configurations.globalIdentifyTraits) {
      configurations.globalIdentifyTraits = {};
    }

    const { globalIdentifyTraits, data } = configurations;

    if (!globalIdentifyTraits.acxiomTrackingProperties) {
      const acxiomTrackingProperties = data.acxiomTrackingProperties.getValue(true);

      const hasValidProperty = acxiomTrackingProperties &&
        Object.values(acxiomTrackingProperties).some(Boolean);

      if (!hasValidProperty) { return null; }

      globalIdentifyTraits.acxiomTrackingProperties = acxiomTrackingProperties;
    }

    configurations.globalIdentifyTraits.g_client_id = configurations.data.googleAnalyticsClientId.getValue(true);
    setDataAttributeTraits(configurations.globalIdentifyTraits);
    setAnonymousId(configurations.data.segmentAnonymousId());
    const timeLapsed = +new Date() - status.time;
    const traits = {
      user_id: getUserId(),
      g_client_id: configurations.globalIdentifyTraits.g_client_id,
      ...configurations.globalIdentifyTraits.acxiomTrackingProperties
    };

    if (traits.Segment === null && timeLapsed < 10000) {
      configurations.globalIdentifyTraits = null;
      return null;
    }

    return traits;
  }

  /**
   * @private
   * getTraits: gets the data from the DOM Node that will be send to Segment.
   *
   * @param {DOMStringMap} dataset properties of the object.
   * @param {*} prefix to evaluate the properties we want to retrieve.
   * @param {*} attribute being evaluated.
   */
  function getTraits(dataset, prefix, attribute) {
    const sharedPrefix = configurations.sharedPrefix;
    if (!attribute.startsWith(prefix) && !attribute.startsWith(sharedPrefix)) {
      return;
    }

    const name = attribute.replace(prefix, '').replace(sharedPrefix, '').toLowerCase();

    if (!name) {
      return;
    }

    this[name] = dataset[attribute];
  }

  /**
   * @private
   * track: fires a segment track event.
   *
   * @param {object} globalTraits information that is global for the event.
   * @param {string} data with the information that is going to be logged.
   */
  function track(globalTraits, data) {
    const event = data[0];
    const propertyName = Object.keys(data[1])[0];
    let value = JSON.parse(JSON.stringify(globalTraits));

    if (data[1][propertyName].length === 1) {
      Object.entries(JSON.parse(JSON.stringify(data[1][propertyName][0]))).forEach(append.bind(value));
    } else {
      value[propertyName] = data[1][propertyName];
    }

    value = cleanEmptyOrNullTraitsBeforeSend(value);
    global.analytics.track(event, value);
  }

  /**
   * @constructs NewHomeSource.Analytics.segment
   */
  function Segment(data) {
    configurations.data = data;
  }

  /**
   * @public
   * eventTracker: send data related to user action events.
   *
   * @param {object} status of the scheduled idle task.
   */
  Segment.eventTracker = function eventTracker(status) {
    if (!global.analytics) {
      return false;
    }

    let traits = createGlobalTraits(status);

    if (!traits) {
      return false;
    }

    const data = this.dataset;
    Object.keys(data).forEach(getTraits.bind(traits, data, configurations.eventPrefix));
    traits = cleanEmptyOrNullTraitsBeforeSend(traits);
    const event = traits.event;
    if (!event) {
      return false;
    }
    delete traits.event;
    global.analytics.track(event, traits);
    return true;
  };

  /**
   * @public
   * eventTrackerValidator: validates if the dataset passed as parameter represents a segment event being tracked.
   */
  Segment.eventTrackerValidator = function eventTrackerValidator(dataset) {
    return !!dataset.segmentEvent;
  };

  /**
   * @public
   * eventTrackerValidator: validates if the dataset passed as parameter represents a segment event being tracked.
   */
  Segment.onDemandTrackerValidator = function onDemandTrackerValidator(data) {
    return data && data.eventType === 'segmentEvent';
  };

  /**
   * @public
   * identityTracker: send data related to user identification events.
   *
   * @param {object} status of the scheduled idle task.
   */
  Segment.identityTracker = function identityTracker(status) {
    if (!global.analytics) {
      return false;
    }
    let identityTraits = createGlobalIdentifyTraits(status);

    if (!identityTraits) {
      return false;
    }

    const data = this.caller.dataset;

    const identityData = this.form.querySelectorAll('[data-segment-identity-data]');
    const length = identityData.length;

    for (let index = 0; index < length; index += 1) {
      const name = identityData[index].dataset.segmentIdentityData;
      const value = identityData[index].type === 'checkbox' || identityData[index].type === 'radio'
        ? identityData[index].checked
        : identityData[index].value;

      if (value !== '') {
        identityTraits[name] = value;
      }
    }

    if (identityTraits.Name) {
      const fullName = identityTraits.Name.split(' ');
      identityTraits.first_name = fullName[0];
      identityTraits.last_name = fullName.slice(1).join(' ');
      delete identityTraits.Name;
      delete data.Name;
    }

    identityTraits.last_event = data.segmentIdentityEvent || data.segmentEventEvent;
    delete identityTraits.event;
    identityTraits = cleanEmptyOrNullTraitsBeforeSend(identityTraits);

    if (identityTraits.user_id) {
      global.analytics.identify(identityTraits.user_id, identityTraits);
      return true;
    }

    identityTraits = cleanEmptyOrNullTraitsBeforeSend(identityTraits);
    global.analytics.identify(identityTraits);
    return true;
  };

  /**
   * @public
   * identityTrackerValidator: validates if the dataset passed as parameter represents a segment identity being tracked.
   */
  Segment.identityTrackerValidator = function identityTrackerValidator(dataset) {
    return !!dataset.segmentIdentity;
  };

  /**
   * @public
   * impressionTracker: send data related to display events.
   *
   * @param {object} status of the scheduled idle task.
   */
  Segment.impressionTracker = function impressionTracker(status) {
    if (!global.analytics) {
      return false;
    }

    if (!this || !this.length) {
      return true;
    }

    const traits = createGlobalTraits(status);
    if (!traits) {
      return false;
    }

    const length = this.length;
    const impressionTypes = {};
    const pendingImpressions = [];

    for (let index = 0; index < length; index += 1) {
      let listingTraits = {};
      const data = this[index].dataset;
      const event = data.segmentImpressionEvent;
      const type = data.segmentImpressionType;

      if (event && type) {
        Object.keys(data).forEach(getTraits.bind(listingTraits, data, configurations.impressionPrefix));
        listingTraits = cleanEmptyOrNullTraitsBeforeSend(listingTraits);
        if (traits.filters_applied == null && listingTraits.includefilters) {
          traits.filters_applied = configurations.globalTraits.filters_applied();
        }

        delete listingTraits.includefilters;
        delete listingTraits.event;
        delete listingTraits.Type;

        impressionTypes[event] = impressionTypes[event] || {};
        impressionTypes[event][type] = impressionTypes[event][type] || [];
        impressionTypes[event][type].push(listingTraits);

        this[index].removeAttribute('data-segment-impression-event');
        this[index].removeAttribute('data-segment-impression-type');
      }
    }

    Object.entries(impressionTypes).forEach(track.bind(null, traits));

    return pendingImpressions.length === 0;
  };

  /**
   * @public
   * impressionTrackerSelector: returns the expression that can be used to query the DOM.
   */
  Segment.impressionTrackerSelector = function impressionTrackerSelector() {
    return 'data-segment-impression';
  };

  /**
   * @public
   * onDemandTracker: send data related to events that cannot be mapped to the HTML.
   *
   * @param {object} status of the scheduled idle task.
   */
  Segment.onDemandTracker = function onDemandTracker(status) {
    if (!global.analytics) {
      return false;
    }

    if (!this || !this.event) {
      return true;
    }

    const self = Object.assign({}, this);

    let trackingGlobalTraits = createGlobalTraits(status);
    let identifyGlobalTraits = createGlobalIdentifyTraits(status);
    const event = self.event;
    const isIdentifyEvent = self.isIdentifyEvent;
    const isIdentifyOnly = self.isIdentifyOnly;

    if (!trackingGlobalTraits || (self.isIdentifyEvent && !identifyGlobalTraits)) {
      return false;
    }

    delete self.eventType;
    delete self.event;
    delete self.isIdentifyEvent;
    delete self.isIdentifyOnly;

    Object.entries(JSON.parse(JSON.stringify(self))).forEach(append.bind(trackingGlobalTraits));

    if (isIdentifyEvent) {
      Object.entries(JSON.parse(JSON.stringify(self))).forEach(append.bind(identifyGlobalTraits));
      identifyGlobalTraits.last_event = event;
      identifyGlobalTraits = cleanEmptyOrNullTraitsBeforeSend(identifyGlobalTraits);

      if (identifyGlobalTraits.user_id) {
        global.analytics.identify(identifyGlobalTraits.user_id, identifyGlobalTraits);
      } else {
        global.analytics.identify(identifyGlobalTraits);
      }

      if (isIdentifyOnly) {
        return true;
      }
    }

    trackingGlobalTraits = cleanEmptyOrNullTraitsBeforeSend(trackingGlobalTraits);
    global.analytics.track(event, trackingGlobalTraits);
    return true;
  };

  /**
   * @public
   *
   * noConflict: Returns control of NewHomeSource.Segment to the previous script.
   */
  Segment.noConflict = function noConflict() {
    global.NewHomeSource.Analytics.segment = previousSegment;
    return Segment;
  };

  global.NewHomeSource.Analytics.segment = Segment;
}(window));
